import React, { useEffect, useRef } from "react";
import { useFormikContext } from "formik";
import { NumberInput, SingleSelect } from "@loanpal/lumos__core";
import { Text } from "theme-ui";
import {
  numberToMoney,
  numberToMoneyNoDecimal,
  moneyStringToNumber,
} from "./format";
import { resetEmbeddedPageScroll } from "./embed-iframe-guard";

const DEBOUNCE_MS = 2000;

function validateLoanAmountDebounced({
  amt,
  values,
  limitations,
  minAmount,
  maxAmount,
  setFieldError,
  setErrors,
  setCanApply,
  onValid,
}) {
  setErrors({});
  if (limitations?.find((el) => el.state === values.state)) {
    const limits = limitations.find((el) => el.state === values.state);
    if (moneyStringToNumber(amt) < limits.minLoanAmount) {
      setFieldError(
        "loanAmount",
        `Loan amount needs to be greater than ${numberToMoneyNoDecimal(limits.minLoanAmount)}`
      );
      setCanApply(false);
      return;
    }
    if (moneyStringToNumber(amt) > limits.maxLoanAmount) {
      setFieldError(
        "loanAmount",
        `Loan amount needs to be less than ${numberToMoneyNoDecimal(limits.maxLoanAmount)}`
      );
      setCanApply(false);
      return;
    }
  } else {
    if (moneyStringToNumber(amt) < minAmount) {
      setFieldError(
        "loanAmount",
        `Loan amount needs to be greater than ${numberToMoneyNoDecimal(minAmount)}`
      );
      setCanApply(false);
      return;
    }
    if (moneyStringToNumber(amt) > maxAmount) {
      setFieldError(
        "loanAmount",
        `Loan amount needs to be less than ${numberToMoneyNoDecimal(maxAmount)}`
      );
      setCanApply(false);
      return;
    }
  }
  onValid();
}

/** Stable state select — must be module-scoped so parent re-renders do not remount it. */
export function ApplyStatesInput({
  statesItems,
  limitations,
  minAmount,
  maxAmount,
  setLoadingPayment,
  setCanApply,
  readOnly = false,
}) {
  const { setFieldError, setErrors, values } = useFormikContext();
  return (
    <SingleSelect
      name="state"
      label="State"
      size={20}
      items={statesItems}
      readOnly={readOnly}
      dangerouslyOnChange={(target) => {
        if (values.loanAmount !== "" && statesItems.length > 1) {
          setLoadingPayment(true);
          setErrors({});
          const amt = values.loanAmount;
          if (limitations?.find((el) => el.state === target)) {
            const limits = limitations.find((el) => el.state === target);
            if (moneyStringToNumber(amt) < limits.minLoanAmount) {
              setFieldError(
                "loanAmount",
                `Loan amount needs to be greater than ${numberToMoneyNoDecimal(limits.minLoanAmount)}`
              );
              setCanApply(false);
            } else if (moneyStringToNumber(amt) > limits.maxLoanAmount) {
              setFieldError(
                "loanAmount",
                `Loan amount needs to be less than ${numberToMoneyNoDecimal(limits.maxLoanAmount)}`
              );
              setCanApply(false);
            } else {
              setLoadingPayment(false);
              setCanApply(true);
            }
          } else {
            if (moneyStringToNumber(amt) < minAmount) {
              setFieldError(
                "loanAmount",
                `Loan amount needs to be greater than ${numberToMoneyNoDecimal(minAmount)}`
              );
              setCanApply(false);
            } else if (moneyStringToNumber(amt) > maxAmount) {
              setFieldError(
                "loanAmount",
                `Loan amount needs to be less than ${numberToMoneyNoDecimal(maxAmount)}`
              );
              setCanApply(false);
            } else {
              setLoadingPayment(false);
              setCanApply(true);
            }
          }
        }
      }}
    />
  );
}

/** Reads loan amount from Formik so the payment card can update without a parent state bump. */
export function FormLoanAmountText(props) {
  const { values } = useFormikContext();
  const amount = moneyStringToNumber(values.loanAmount);
  return (
    <Text variant="h3" as="h2" {...props}>
      {numberToMoney(Number.isNaN(amount) ? 0 : amount)}
    </Text>
  );
}

/** Loan-amount calculator apply forms (apply, goodleapplus, sunrun). */
export function LoanAmountInput({
  isEmbedded = false,
  readOnly = false,
  singleOffer,
  limitations,
  minAmount,
  maxAmount,
  setPaymentAmount,
  setCanApply,
}) {
  const { setFieldValue, setFieldError, setErrors, values } = useFormikContext();
  const debounceRef = useRef(null);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  return (
    <NumberInput
      key="loanAmount"
      label="Amount to finance"
      name="loanAmount"
      autoComplete="off"
      readOnly={readOnly}
      onFocusCapture={(e) => {
        if (!isEmbedded) return;
        if (document.documentElement.getAttribute("data-embed-lock-scroll") !== "true") return;
        e.target.blur();
        resetEmbeddedPageScroll();
      }}
      onBlurCapture={({ target }) => {
        const moneyValue = moneyStringToNumber(target.value);
        if (Number.isNaN(moneyValue)) return;
        setFieldValue("loanAmount", numberToMoney(moneyValue));
      }}
      onChangeCapture={({ target }) => {
        const amt = target.value;
        setFieldValue("loanAmount", amt, false);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
          if (!singleOffer) return;
          validateLoanAmountDebounced({
            amt,
            values,
            limitations,
            minAmount,
            maxAmount,
            setFieldError,
            setErrors,
            setCanApply,
            onValid: () => {
              setPaymentAmount({
                amount: moneyStringToNumber(amt),
                payment: true,
              });
              setCanApply(true);
            },
          });
        }, DEBOUNCE_MS);
      }}
    />
  );
}

function validatePaymentAmountDebounced({
  amt,
  values,
  limitations,
  minAmount,
  maxAmount,
  singleOffer,
  getPaymentFactor,
  resolveLoanAmount,
  setFieldError,
  setErrors,
  setCanApply,
  onValid,
}) {
  setErrors({});
  const pf = getPaymentFactor(singleOffer);
  const computedLoan = moneyStringToNumber(resolveLoanAmount(amt, pf));

  if (limitations?.find((el) => el.state === values.state)) {
    const limits = limitations.find((el) => el.state === values.state);
    if (computedLoan < limits.minLoanAmount) {
      setFieldError(
        "paymentAmount",
        `Calculated loan amount needs to be greater than ${numberToMoneyNoDecimal(limits.minLoanAmount)}`
      );
      setCanApply(false);
      return;
    }
    if (computedLoan > limits.maxLoanAmount) {
      setFieldError(
        "paymentAmount",
        `Calculated loan amount needs to be less than ${numberToMoneyNoDecimal(limits.maxLoanAmount)}`
      );
      setCanApply(false);
      return;
    }
  } else {
    if (computedLoan < minAmount) {
      setFieldError(
        "paymentAmount",
        `Calculated loan amount needs to be greater than ${numberToMoneyNoDecimal(minAmount)}`
      );
      setCanApply(false);
      return;
    }
    if (computedLoan > maxAmount) {
      setFieldError(
        "paymentAmount",
        `Calculated loan amount needs to be less than ${numberToMoneyNoDecimal(maxAmount)}`
      );
      setCanApply(false);
      return;
    }
  }
  onValid(computedLoan);
}

/** Payment-amount calculator apply form (daikin). */
export function PaymentAmountInput({
  singleOffer,
  limitations,
  minAmount,
  maxAmount,
  calculatorType,
  setPaymentAmountObj,
  setCanApply,
  getPaymentFactor,
  resolveLoanAmount,
  label = "What is your budget for a monthly payment?",
}) {
  const { setFieldValue, setFieldError, setErrors, values } = useFormikContext();
  const debounceRef = useRef(null);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  return (
    <NumberInput
      key="paymentAmount"
      label={label}
      name="paymentAmount"
      autoComplete="off"
      onBlurCapture={({ target }) => {
        const moneyValue = moneyStringToNumber(target.value);
        if (Number.isNaN(moneyValue)) return;
        setFieldValue("paymentAmount", numberToMoney(moneyValue));
      }}
      onChangeCapture={({ target }) => {
        const moneyValue = moneyStringToNumber(target.value);
        const amt = target.value;
        setPaymentAmountObj({
          paymentAmount: moneyStringToNumber(target.value),
          loanAmount: 0,
          payment: false,
          monthly: calculatorType,
        });
        setFieldValue("paymentAmount", numberToMoney(moneyValue));
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
          if (!singleOffer) return;
          validatePaymentAmountDebounced({
            amt,
            values,
            limitations,
            minAmount,
            maxAmount,
            singleOffer,
            getPaymentFactor,
            resolveLoanAmount,
            setFieldError,
            setErrors,
            setCanApply,
            onValid: (computedLoan) => {
              setPaymentAmountObj({
                paymentAmount: moneyStringToNumber(amt),
                loanAmount: computedLoan,
                payment: true,
                monthly: calculatorType,
              });
              setCanApply(true);
            },
          });
        }, DEBOUNCE_MS);
      }}
    />
  );
}
